import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { profile } from '../data';

const links = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Work', to: '/work' },
  { label: 'Company', to: '/company' },
  { label: 'Contact', to: '/contact' },
];

function isActive(pathname, to) {
  return to === '/' ? pathname === '/' : pathname.startsWith(to);
}

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const progressRef = useRef(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollableHeight > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollableHeight)) : 0;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${progress})`;
      setScrolled(window.scrollY > 18);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('pageshow', onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('pageshow', onScroll);
    };
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className={`site-header ${scrolled ? 'site-header--scrolled' : ''}`}>
      <div ref={progressRef} className="site-progress" />
      <nav className="nav-shell" aria-label="Primary navigation">
        <Link className="brand" to="/" data-cursor-label="HOME">
          <span className="brand__mark" aria-hidden="true">PSHR</span>
          <span className="brand__copy">
            <span>{profile.shortName}</span><small>Field notes / 26</small>
          </span>
        </Link>

        <div className="nav-links nav-links--desktop">
          {links.map((item, index) => {
            const active = isActive(location.pathname, item.to);
            return (
              <Link key={item.to} className={`nav-link ${active ? 'is-active' : ''}`} to={item.to} aria-current={active ? 'page' : undefined} data-cursor-label={item.label.toUpperCase()}>
                <small>0{index + 1}</small><span>{item.label}</span>
                {active ? <span className="nav-link__indicator" /> : null}
              </Link>
            );
          })}
        </div>

        <Link className="nav-contact" to="/contact" data-cursor-label="HELLO">
          <span>Start a brief</span><span aria-hidden="true">↗</span>
        </Link>

        <button
          className="menu-toggle"
          type="button"
          aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={open}
          aria-controls="mobile-navigation"
          onClick={() => setOpen((value) => !value)}
        >
          <span />
          <span />
        </button>
      </nav>

      <div className={`mobile-menu-wrap ${open ? 'is-open' : ''}`} aria-hidden={!open}>
        <button className="mobile-menu-backdrop" type="button" aria-label="Close navigation menu" onClick={() => setOpen(false)} />
        <div id="mobile-navigation" className="mobile-menu">
          <p className="eyebrow">Index / navigate</p>
          {links.map((item, index) => {
            const active = isActive(location.pathname, item.to);
            return (
              <div key={item.to} className="mobile-menu__item" style={{ '--menu-item-index': index }}>
                <Link className={`mobile-menu__link ${active ? 'is-active' : ''}`} to={item.to} aria-current={active ? 'page' : undefined} data-cursor-label={item.label.toUpperCase()} tabIndex={open ? 0 : -1}>
                  <small>0{index + 1}</small><span>{item.label}</span><i aria-hidden="true">↗</i>
                </Link>
              </div>
            );
          })}
          <a className="mobile-menu__detail" href={`mailto:${profile.email}`} tabIndex={open ? 0 : -1}>{profile.email}</a>
        </div>
      </div>
    </header>
  );
}
